import { useState, useEffect } from "react";

const STORAGE_KEY = "theme";

// All known theme class names. Add new themes here when created.
const KNOWN_THEMES = ["dark"] as const;
type Theme = (typeof KNOWN_THEMES)[number] | "";

function applyTheme(theme: Theme) {
  const html = document.documentElement;
  // Remove every known theme class, then add the new one (if any).
  // This keeps any other classes on <html> untouched.
  html.classList.remove(...KNOWN_THEMES);
  if (theme) html.classList.add(theme);
}

function getSystemPreference(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}

function getInitialTheme(): Theme {
  if (typeof window === "undefined") return "";
  const stored = localStorage.getItem(STORAGE_KEY);
  // Explicit user preference
  if (stored === "dark") return "dark";
  if (stored === "light" || stored === "") return "";
  // No stored preference — follow OS
  return getSystemPreference() ? "dark" : "";
}

export function useDarkMode() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  // Apply theme class to <html> whenever it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Follow OS preference changes only when user hasn't set an explicit choice
  useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia("(prefers-color-scheme: dark)");
    } catch {
      return;
    }
    const handler = (e: MediaQueryListEvent) => {
      if (!localStorage.getItem(STORAGE_KEY)) {
        setTheme(e.matches ? "dark" : "");
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const toggle = () => {
    setTheme((prev) => {
      const next: Theme = prev === "dark" ? "" : "dark";
      localStorage.setItem(STORAGE_KEY, next === "" ? "light" : next);
      return next;
    });
  };

  // setTheme allows switching to any theme (e.g. "theme-purple" in the future)
  const switchTheme = (next: Theme) => {
    localStorage.setItem(STORAGE_KEY, next === "" ? "light" : next);
    setTheme(next);
  };

  return { isDark: theme === "dark", theme, toggle, switchTheme };
}
